import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import isEqual from 'lodash/isEqual'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { ITier } from '@forwardflow/types/subscription'
import {
   IBusinessHours,
   IAppointmentTimeFrame,
   TOnboardingStep,
   IBusinessDetails,
   IStageMapGroup,
   TFubUser,
   ITempStageGroup,
   TStageGroupId,
   TStopStageKey,
   ICampaignConfiguration,
} from '@forwardflow/types/onboarding'
import {
   initialAvailabilityDays,
   initialCampaignConfiguration,
   initialStageMappingsGroups,
   initialTempStageGroups,
   ONBOARDING_STEPS,
} from '@forwardflow/constants/onboarding.constants'

export interface IOnboardingData {
   email: string | null
   currentStep: TOnboardingStep
   organizationId: string | null
   isFUBConnected: boolean
   isSubscriptionActivated: boolean
   isOnBoardingCompleted: boolean
   inboxAppInstallRequest: boolean
   isCampaignConfigured: boolean
   selectedTier?: ITier | null
   planPeriod: 'monthly' | 'yearly'
   businessDetails: IBusinessDetails
   businessHours: IBusinessHours
   appointmentTimeframe: IAppointmentTimeFrame
   stageMappings: IStageMapGroup[]
   tempStageGroups: ITempStageGroup[]
   invitedUsers: TFubUser[]
   campaignConfiguration: ICampaignConfiguration
}

export type IStage = {
   id: string
   name: string
   description: string
   type?: 'stage' | 'tag'
}

export interface ITag {
   name: string
   description: string
   id: string
   type?: 'tag'
}

interface IOnboardingStoreV2 {
   onboardingData: IOnboardingData
   stages: IStage[]
   tags: string[]
   sources: string[]
   ponds: string[]
   setOnboardingData: (data: IOnboardingData) => void
   updateOnboardingData: (data: IOnboardingData) => void
   setCurrentStep: (step: TOnboardingStep) => void
   getCurrentStep: () => TOnboardingStep
   getNextStep: () => TOnboardingStep
   getPreviousStep: () => TOnboardingStep
   setOrganizationId: (organizationId: string) => void
   setIsFUBConnected: (isFUBConnected: boolean) => void
   setIsSubscriptionActivated: (isSubscriptionActivated: boolean) => void
   setIsOnBoardingCompleted: (isOnBoardingCompleted: boolean) => void
   initializeOnboardingData: (organizationId: string) => Promise<void>
   setEmail: (email: string) => void
   setSelectedTier: (selectedTier: Partial<ITier> | null) => void
   setPlanPeriod: (planPeriod: 'monthly' | 'yearly') => void
   setIsLoadingOrInitialization: (isLoading: boolean) => void
   isLoadingOrInitialization: boolean
   setBusinessHours: (businessHours: IBusinessHours) => void
   setAppointmentTimeframe: (
      appointmentTimeframe: IAppointmentTimeFrame
   ) => void
   setBusinessDetails: (businessDetails: IBusinessDetails) => void
   setStages: (stages: IStage[]) => void
   setStageMappings: (stageMappings: IStageMapGroup[]) => void
   setInvitedUsers: (invitedUsers: TFubUser[]) => void
   getUnselectedStages: () => IStage[]
   getUnSelectedTags: () => string[]
   goToStep: (step: TOnboardingStep) => void
   setTags: (tags: string[]) => void
   setSources: (sources: string[]) => void
   setPonds: (ponds: string[]) => void
   setInboxAppInstallRequest: (inboxAppInstallRequest: boolean) => void
   setTempStageGroupByGroupId: (
      groupId: TStageGroupId | TStopStageKey,
      group: ITempStageGroup
   ) => void
   setTempStagesByGroupId: (
      groupId: TStageGroupId | TStopStageKey,
      stagesOrTags: Array<IStage | ITag>
   ) => void
   getTempStagesByGroupId: (
      groupId: TStageGroupId | TStopStageKey
   ) => Array<IStage | ITag>
   setCampaignConfiguration: (
      campaignConfiguration: Partial<ICampaignConfiguration>
   ) => void
   setIsCampaignConfigured: (isCampaignConfigured: boolean) => void
   getFirstFilterCriteria: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY'
   } | null
   getRestFilterCriterias: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY'
   }[]
   setFirstFilterCriteria: (firstFilterCriteria: {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY'
   }) => void
}

const isStageSelected = (stage: IStage, tempStageGroups: ITempStageGroup[]) => {
   // Check in regular stages
   const isInRegularStages = tempStageGroups.some((group) =>
      group.stageOrTags?.some(
         (item) => String(item.id) === String(stage.id) && item.type === 'stage'
      )
   )
   if (isInRegularStages) return true

   // Check in types (for stop stages)
   const isInTypes = tempStageGroups.some(
      (group) =>
         group.type === 'appointment-field' &&
         group.stageOrTags?.some(
            (item) =>
               String(item.id) === String(stage.id) && item.type === 'stage'
         )
   )
   if (isInTypes) return true

   return false
}

const isTagSelected = (tag: string, tempStageGroups: ITempStageGroup[]) => {
   return tempStageGroups.some((group) =>
      group.stageOrTags?.some(
         (item) => String(item.name) === String(tag) && item.type === 'tag'
      )
   )
}

export const useOnboardingStoreV2 = create<IOnboardingStoreV2>()(
   subscribeWithSelector(
      devtools((set, get) => ({
         onboardingData: {
            email: null,
            currentStep: 'plan-selection',
            organizationId: null,
            isFUBConnected: false,
            isSubscriptionActivated: false,
            selectedTier: null,
            planPeriod: 'monthly',
            businessDetails: {
               businessOwnerName: '',
               businessOwnerTitle: '',
               legalBusinessName: '',
               friendlyBusinessName: '',
               introductionTemplate: '',
            },
            businessHours: {
               timezone: '',
               allowAgentsToSetAvailability: false,
               days: initialAvailabilityDays,
               isSameForAllDays: true,
            },
            appointmentTimeframe: {
               timezone: '',
               appointmentBufferTime: 0,
               appointmentType: 'virtual',
               appointmentLocationType: 'in-office',
               meetingPlatform: 'zoom',
               meetingLength: 0,
               meetingLink: '',
               allowAgentsToSetAvailability: false,
               days: initialAvailabilityDays,
               isSameForAllDays: true,
            },
            isOnBoardingCompleted: false,
            inboxAppInstallRequest: false,
            isCampaignConfigured: false,
            stageMappings: initialStageMappingsGroups,
            tempStageGroups: initialTempStageGroups,
            invitedUsers: [],
            campaignConfiguration: initialCampaignConfiguration,
         },
         setInboxAppInstallRequest: (inboxAppInstallRequest: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  inboxAppInstallRequest,
               },
            })),
         setCampaignConfiguration: (
            campaignConfiguration: Partial<ICampaignConfiguration>
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  campaignConfiguration: {
                     ...state.onboardingData.campaignConfiguration,
                     ...campaignConfiguration,
                  },
               },
            })),
         getFirstFilterCriteria: () => {
            const { campaignConfiguration } = get().onboardingData
            const criteria =
               campaignConfiguration?.campaign_filters?.eligible_criteria?.[0]
            if (!criteria) return null
            // Ensure the value is always a string[]
            return {
               ...criteria,
               value: Array.isArray(criteria.value)
                  ? criteria.value.map(String)
                  : [],
            }
         },
         getRestFilterCriterias: () => {
            const { campaignConfiguration } = get().onboardingData
            const criterias =
               campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(
                  1
               )
            if (!criterias) return []
            return criterias.map((criteria) => ({
               ...criteria,
               value: Array.isArray(criteria.value)
                  ? criteria.value.map(String)
                  : [],
            }))
         },
         setFirstFilterCriteria: (firstFilterCriteria: {
            field: string
            value: string[]
            logic: 'AND'
            operator: 'CONTAINS_ANY'
         }) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  campaignConfiguration: {
                     ...state.onboardingData.campaignConfiguration,
                     campaign_filters: {
                        ...state.onboardingData.campaignConfiguration
                           ?.campaign_filters,
                        eligible_criteria: [
                           {
                              field: firstFilterCriteria.field as
                                 | 'source'
                                 | 'stage'
                                 | 'tags'
                                 | 'assigned_pond_id',
                              value: firstFilterCriteria.value,
                              logic: firstFilterCriteria.logic,
                              operator: firstFilterCriteria.operator,
                           },
                           ...(state.onboardingData.campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(
                              1
                           ) ?? []),
                        ],
                     },
                  },
               },
            })),
         setIsCampaignConfigured: (isCampaignConfigured: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isCampaignConfigured,
               },
            })),
         setInvitedUsers: (invitedUsers: TFubUser[]) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, invitedUsers },
            })),
         getUnselectedStages: () => {
            const { stages, onboardingData } = get()
            const { tempStageGroups } = onboardingData

            if (!stages || !tempStageGroups) return []

            return stages.filter(
               (stage) => !isStageSelected(stage, tempStageGroups)
            )
         },
         getUnSelectedTags: () => {
            const { tags, onboardingData } = get()
            const { tempStageGroups } = onboardingData

            if (!tags || !tempStageGroups) return []

            return tags.filter((tag) => !isTagSelected(tag, tempStageGroups))
         },
         setStageMappings: (stageMappings: IStageMapGroup[]) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, stageMappings },
            })),
         stages: [],
         tags: [],
         sources: [],
         ponds: [],
         setStages: (stages: IStage[]) => set({ stages }),
         setTags: (tags: string[]) => set({ tags }),
         setSources: (sources: string[]) => set({ sources }),
         setPonds: (ponds: string[]) => set({ ponds }),
         isLoadingOrInitialization: true,
         goToStep: (step: TOnboardingStep) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, currentStep: step },
            })),
         setBusinessDetails: (businessDetails: IBusinessDetails) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, businessDetails },
            })),
         setOnboardingData: (data) => set({ onboardingData: data }),
         setOrganizationId: (organizationId: string) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, organizationId },
            })),
         updateOnboardingData: (data: Partial<IOnboardingData>) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, ...data },
            })),
         setCurrentStep: (step: TOnboardingStep) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, currentStep: step },
            })),
         getCurrentStep: () => get().onboardingData.currentStep,
         setTempStagesByGroupId: (
            groupId: TStageGroupId | TStopStageKey,
            stagesOrTags: Array<IStage | ITag>
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  tempStageGroups: state.onboardingData.tempStageGroups.map(
                     (group) =>
                        group.groupId === groupId
                           ? { ...group, stageOrTags: stagesOrTags }
                           : group
                  ),
               },
            })),
         setTempStageGroupByGroupId: (
            groupId: TStageGroupId | TStopStageKey,
            group: ITempStageGroup
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  tempStageGroups: state.onboardingData.tempStageGroups.map(
                     (g) => (g.groupId === groupId ? group : g)
                  ),
               },
            })),
         getTempStagesByGroupId: (groupId: TStageGroupId | TStopStageKey) => {
            const { tempStageGroups } = get().onboardingData
            const group = tempStageGroups?.find(
               (group) => group.groupId === groupId
            )
            if (!group) return []
            return group.stageOrTags
         },
         getNextStep: () => {
            const currentStep = get().getCurrentStep()
            const { isFUBConnected, isSubscriptionActivated } =
               get().onboardingData
            const index = ONBOARDING_STEPS.indexOf(currentStep)

            if (index === ONBOARDING_STEPS.length - 1) {
               return currentStep
            }

            let nextIndex = index + 1
            let nextStep = ONBOARDING_STEPS[nextIndex]

            // Skip all first three steps if subscription is already activated
            if (
               isSubscriptionActivated &&
               ['plan-selection', 'fub-connect', 'payment-details'].includes(
                  nextStep
               )
            ) {
               while (
                  nextIndex < ONBOARDING_STEPS.length &&
                  ['plan-selection', 'fub-connect', 'payment-details'].includes(
                     ONBOARDING_STEPS[nextIndex]
                  )
               ) {
                  nextIndex++
               }
               nextStep = ONBOARDING_STEPS[nextIndex]
            }

            // Skip fub-connect step if FUB is already connected
            if (
               !isSubscriptionActivated &&
               isFUBConnected &&
               nextStep === 'fub-connect'
            ) {
               nextIndex++
               nextStep = ONBOARDING_STEPS[nextIndex]
            }

            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  currentStep: nextStep,
               },
            }))
            return nextStep
         },
         setBusinessHours: (businessHours: IBusinessHours) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, businessHours },
            })),
         setAppointmentTimeframe: (
            appointmentTimeframe: IAppointmentTimeFrame
         ) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  appointmentTimeframe,
               },
            })),
         getPreviousStep: () => {
            const currentStep = get().getCurrentStep()
            const { isFUBConnected, isSubscriptionActivated } =
               get().onboardingData
            const index = ONBOARDING_STEPS.indexOf(currentStep)

            if (index === 0) {
               return currentStep
            }

            let previousIndex = index - 1
            let previousStep = ONBOARDING_STEPS[previousIndex]

            // Skip fub-connect step if FUB is already connected
            if (
               !isSubscriptionActivated &&
               isFUBConnected &&
               previousStep === 'fub-connect'
            ) {
               previousIndex--
               previousStep = ONBOARDING_STEPS[previousIndex]
            }

            // Skip all first three steps if subscription is already activated
            if (
               isSubscriptionActivated &&
               ['plan-selection', 'fub-connect', 'payment-details'].includes(
                  previousStep
               )
            ) {
               while (
                  previousIndex >= 0 &&
                  ['plan-selection', 'fub-connect', 'payment-details'].includes(
                     ONBOARDING_STEPS[previousIndex]
                  )
               ) {
                  previousIndex--
               }
               // If we've gone past the beginning, stay at the current step
               if (previousIndex < 0) {
                  return currentStep
               }
               previousStep = ONBOARDING_STEPS[previousIndex]
            }

            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  currentStep: previousStep,
               },
            }))
            return previousStep
         },
         setIsFUBConnected: (isFUBConnected: boolean) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, isFUBConnected },
            })),
         setIsSubscriptionActivated: (isSubscriptionActivated: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isSubscriptionActivated,
               },
            })),
         setIsOnBoardingCompleted: (isOnBoardingCompleted: boolean) =>
            set((state) => ({
               onboardingData: {
                  ...state.onboardingData,
                  isOnBoardingCompleted,
               },
            })),
         setEmail: (email: string) =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, email },
            })),
         setSelectedTier: (selectedTier: Partial<ITier> | null) =>
            set(
               (state) =>
                  ({
                     onboardingData: {
                        ...state.onboardingData,
                        selectedTier: {
                           ...state.onboardingData.selectedTier,
                           ...selectedTier,
                        },
                     },
                  } as IOnboardingStoreV2)
            ),
         setPlanPeriod: (planPeriod: 'monthly' | 'yearly') =>
            set((state) => ({
               onboardingData: { ...state.onboardingData, planPeriod },
            })),
         setIsLoadingOrInitialization: (isLoadingOrInitialization: boolean) =>
            set({ isLoadingOrInitialization }),
         initializeOnboardingData: async (organizationId: string) => {
            set({ isLoadingOrInitialization: true })

            try {
               const response = await OnboardingV2Service.getOnboardingState({
                  organizationId,
               })

               if (response?.data) {
                  set({
                     onboardingData: {
                        ...response.data, // Always use our local steps definition
                        organizationId,
                     },
                     isLoadingOrInitialization: false,
                  })
               } else {
                  // If no data found, just set the organizationId
                  set((state) => ({
                     onboardingData: {
                        ...state.onboardingData,
                        organizationId,
                     },
                     isLoadingOrInitialization: false,
                  }))
               }
            } catch (error) {
               console.error('Failed to initialize onboarding data:', error)
               // Set organizationId even on error
               set((state) => ({
                  onboardingData: {
                     ...state.onboardingData,
                     organizationId,
                  },
                  isLoadingOrInitialization: false,
               }))
            }
         },
      }))
   )
)

// Auto-sync onboardingData to backend whenever it changes
let syncTimeout: NodeJS.Timeout | null = null
useOnboardingStoreV2.subscribe(
   (state) => state.onboardingData,
   (onboardingData: IOnboardingData) => {
      const isLoading =
         useOnboardingStoreV2.getState().isLoadingOrInitialization

      // Don't sync if loading (to avoid race conditions during initialization)
      if (isLoading) {
         return
      }

      // Only sync if we have an organizationId
      if (!onboardingData.organizationId) {
         return
      }

      // Debounce the sync to avoid too many API calls
      if (syncTimeout) {
         clearTimeout(syncTimeout)
      }

      syncTimeout = setTimeout(async () => {
         try {
            await OnboardingV2Service.updateOnboardingState({
               organizationId: onboardingData.organizationId!,
               data: {
                  ...onboardingData,
               },
            })
         } catch (error) {
            console.error('Failed to sync onboarding data:', error)
         }
      }, 500) // 500ms debounce
   },
   {
      equalityFn: (a: IOnboardingData, b: IOnboardingData) => isEqual(a, b),
   }
)
